import EmptyFolder from 'assets/img/app/empty-folder.png';
import { SidebarMenu, SolidButton } from "components";
import { useEffect, useRef, useState, useCallback, memo, useMemo } from "react";
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControlLabel,
    Pagination,
    CircularProgress,
    TextField,
    Typography,
    Card,
    CardContent,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Avatar,
    Badge,
    Box,
    List,
    ListItem,
    Stack,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StoreIcon from '@mui/icons-material/Store';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotesIcon from '@mui/icons-material/Notes';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import axios from 'axios';
import { useAppSelector } from 'hooks';

// Componente memoizado para evitar re-renders innecesarios
const ProductRow = memo(({ product, sucursalKey, index }: { product: any, sucursalKey: string, index: number }) => (
    <TableRow key={`${sucursalKey}_${index}`} hover>
        <TableCell>
            <Stack direction="row" spacing={1} alignItems="center">
                <InventoryIcon fontSize="small" color="action" />
                <Typography variant="body2">{product.name}</Typography>
            </Stack>
        </TableCell>
        <TableCell align="right">
            <Chip label={product.quantity} size="small" color="primary" variant="outlined" />
        </TableCell>
    </TableRow>
));

ProductRow.displayName = 'ProductRow';

// Componente memoizado para Sucursal Card
const SucursalCard = memo(({
    sucursal,
    sucursalKey,
    isSucursalExpanded,
    onToggle
}: {
    sucursal: any,
    sucursalKey: string,
    isSucursalExpanded: boolean,
    onToggle: () => void
}) => (
    <Card variant="outlined" sx={{ mb: 1, bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <StoreIcon color="action" sx={{ fontSize: 20 }} />
                    <Typography variant="body2" fontWeight="500">{sucursal.name}</Typography>
                </Stack>
                <Chip
                    icon={<CheckCircleIcon />}
                    label={sucursal.created}
                    color="success"
                    size="small"
                    variant="outlined"
                />
                <IconButton
                    size="small"
                    onClick={onToggle}
                >
                    <ExpandMoreIcon sx={{ transform: isSucursalExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />
                </IconButton>
            </Box>

            <Collapse in={isSucursalExpanded} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {sucursal.products?.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography variant="subtitle2" fontWeight="600">Producto</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="subtitle2" fontWeight="600">Cantidad</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sucursal.products.map((product: any, i: number) => (
                                        <ProductRow key={i} product={product} sucursalKey={sucursalKey} index={i} />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No hay registro de que se ingresara productos de Stock para esta sucursal.
                        </Typography>
                    )}
                    {sucursal.notes?.notes && (
                        <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: '#fff6baff', borderStyle: 'dashed' }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <NotesIcon color="warning" fontSize="small" />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="600" color="warning.dark">Notas:</Typography>
                                    <Typography variant="body2">{sucursal.notes.notes}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                </Box>
            </Collapse>
        </CardContent>
    </Card>
), (prevProps, nextProps) => {
    return prevProps.sucursalKey === nextProps.sucursalKey &&
        prevProps.isSucursalExpanded === nextProps.isSucursalExpanded &&
        prevProps.sucursal === nextProps.sucursal;
});

SucursalCard.displayName = 'SucursalCard';

// Componente memoizado para Date Card (usado en vista por Display)
const DateCard = memo(({
    dateData,
    dateKey,
    isDateExpanded,
    expandedSucursals,
    onToggleDate,
    onToggleSucursal
}: {
    dateData: any,
    dateKey: string,
    isDateExpanded: boolean,
    expandedSucursals: { [key: string]: boolean },
    onToggleDate: () => void,
    onToggleSucursal: (key: string) => void
}) => (
    <Card variant="outlined" sx={{ mb: 1, bgcolor: 'grey.50' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Avatar sx={{ bgcolor: '#bebebeff', width: 28, height: 28 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body1" fontWeight="500">{dateData.date}</Typography>
                </Stack>
                <Badge badgeContent={dateData.sucursals?.length}
                    color="primary"
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: '#1865ff',
                        }
                    }}>
                    <IconButton
                        size="small"
                        onClick={onToggleDate}
                    >
                        <ExpandMoreIcon sx={{ transform: isDateExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />
                    </IconButton>
                </Badge>
            </Box>

            <Collapse in={isDateExpanded} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {dateData.sucursals?.map((sucursal: any, id: number) => {
                        const sucursalKey = `${dateKey}_${id}`;
                        const isSucursalExpanded = expandedSucursals[sucursalKey] || false;

                        return (
                            <SucursalCard
                                key={sucursalKey}
                                sucursal={sucursal}
                                sucursalKey={sucursalKey}
                                isSucursalExpanded={isSucursalExpanded}
                                onToggle={() => onToggleSucursal(sucursalKey)}
                            />
                        );
                    })}
                </Box>
            </Collapse>
        </CardContent>
    </Card>
), (prevProps, nextProps) => {
    return prevProps.dateKey === nextProps.dateKey &&
        prevProps.isDateExpanded === nextProps.isDateExpanded &&
        prevProps.dateData === nextProps.dateData &&
        prevProps.expandedSucursals === nextProps.expandedSucursals;
});

DateCard.displayName = 'DateCard';

// Componente memoizado para Display Card
const DisplayCard = memo(({
    display,
    displayKey,
    isDisplayExpanded,
    expandedSucursals,
    onToggleDisplay,
    onToggleSucursal
}: {
    display: any,
    displayKey: string,
    isDisplayExpanded: boolean,
    expandedSucursals: { [key: string]: boolean },
    onToggleDisplay: () => void,
    onToggleSucursal: (key: string) => void
}) => (
    <Card variant="outlined" sx={{ mb: 1, bgcolor: 'grey.50' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Avatar sx={{ bgcolor: '#bebebeff', width: 28, height: 28 }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body1" fontWeight="500">{display.name}</Typography>
                </Stack>
                <Badge
                    badgeContent={display.sucursals?.length}
                    color="primary"
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: '#1865ff',
                        }
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={onToggleDisplay}
                    >
                        <ExpandMoreIcon sx={{ transform: isDisplayExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />
                    </IconButton>

                </Badge>
            </Box>

            <Collapse in={isDisplayExpanded} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {display.sucursals.map((sucursal: any, id: number) => {
                        const sucursalKey = `${displayKey}_${id}`;
                        const isSucursalExpanded = expandedSucursals[sucursalKey] || false;

                        return (
                            <SucursalCard
                                key={sucursalKey}
                                sucursal={sucursal}
                                sucursalKey={sucursalKey}
                                isSucursalExpanded={isSucursalExpanded}
                                onToggle={() => onToggleSucursal(sucursalKey)}
                            />
                        );
                    })}
                </Box>
            </Collapse>
        </CardContent>
    </Card>
), (prevProps, nextProps) => {
    return prevProps.displayKey === nextProps.displayKey &&
        prevProps.isDisplayExpanded === nextProps.isDisplayExpanded &&
        prevProps.display === nextProps.display &&
        prevProps.expandedSucursals === nextProps.expandedSucursals;
});

DisplayCard.displayName = 'DisplayCard';

export const InventoryPage = () => {
    const { token } = useAppSelector(state => state.user);
    const today = new Date();
    const recordsPerView = 10;
    const MAX_RANGE_MONTHS = 6;

    // Calcular la fecha mínima del sistema (6 meses atrás)
    const systemMin = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 6);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // Inicializar el rango por defecto (último mes)
    const defaultStart = (() => {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    })();

    // Función auxiliar para agregar meses a una fecha
    const addMonths = (date: Date, months: number) => {
        const d = new Date(date.getTime());
        const day = d.getDate();
        d.setMonth(d.getMonth() + months);
        if (d.getDate() < day) {
            d.setDate(0);
        }
        return d;
    };

    // Función para normalizar el rango de fechas respetando el límite de 6 meses
    const normalizeWithAnchor = (start: Date, end: Date, anchor: 'start' | 'end') => {
        let s = start;
        let e = end;

        if (anchor === 'start') {
            const maxEnd = addMonths(s, MAX_RANGE_MONTHS);
            const cappedMaxEnd = maxEnd > today ? today : maxEnd;
            if (e < s) e = cappedMaxEnd;
            if (e > cappedMaxEnd) e = cappedMaxEnd;
        } else {
            // ensure start is valid (>= systemMin) and end - start <= MAX_RANGE_MONTHS
            const minStart = addMonths(e, -MAX_RANGE_MONTHS);
            const cappedMinStart = minStart < systemMin ? systemMin : minStart;

            // Si start es mayor que end, ajustarlo
            if (s > e) {
                s = cappedMinStart;
            } else if (s < cappedMinStart) {
                // Solo ajustar si el rango excede los 6 meses
                s = cappedMinStart;
            }
            // Si start >= systemMin y está dentro del rango de 6 meses, mantenerlo
        }

        return [s, e] as const;
    };

    // estado para almacenar los benchmarks recuperados
    const [report, setReport] = useState<any[]>([]);
    const [backupReport, setBackupReports] = useState<any[]>([]);


    // estados para manejar las pantallas de carga
    const [loading, setLoading] = useState<boolean>(true);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);


    // estado que almacena el rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(defaultStart);
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any, sucursal: any }>({ user: [], sucursal: [] });

    // Estado para el modo de vista: 'date' (por fecha) o 'display' (por display)
    const [viewMode, setViewMode] = useState<'date' | 'display'>('date');
    // Estados para expansión de cards
    const [expandedDisplays, setExpandedDisplays] = useState<{ [key: string]: boolean }>({});
    const [expandedSucursals, setExpandedSucursals] = useState<{ [key: string]: boolean }>({});

    // Estados para búsqueda y paginación de filtros
    const [displaySearch, setDisplaySearch] = useState<string>('');
    const [displayPage, setDisplayPage] = useState<number>(1);
    const [sucursalSearch, setSucursalSearch] = useState<string>('');
    const [sucursalPage, setSucursalPage] = useState<number>(1);
    const filtersPerPage = 8;
    const contentRef = useRef<HTMLDivElement>(null);

    // Handlers optimizados con useCallback
    const handleToggleDisplay = useCallback((displayKey: string) => {
        setExpandedDisplays(prev => ({ ...prev, [displayKey]: !prev[displayKey] }));
    }, []);

    const handleToggleSucursal = useCallback((sucursalKey: string) => {
        setExpandedSucursals(prev => ({ ...prev, [sucursalKey]: !prev[sucursalKey] }));
    }, []);

    // Memoizar datos reorganizados por display para evitar recalcular en cada render
    const reportByDisplay = useMemo(() => {
        if (!report || report.length === 0) return [];

        const displayMap = new Map<string, any>();

        report.forEach(dateReport => {
            dateReport.displays?.forEach((display: any) => {
                const displayKey = display.displayKey || display.userId || display.id || display.name;

                if (!displayMap.has(displayKey)) {
                    displayMap.set(displayKey, {
                        displayName: display.name,
                        displayKey: displayKey,
                        dates: []
                    });
                }

                const displayData = displayMap.get(displayKey);
                displayData.dates.push({
                    date: dateReport.date,
                    sucursals: display.sucursals || []
                });
            });
        });

        return Array.from(displayMap.values());
    }, [report]);

    // Memoizar listas paginadas para evitar recalcular en cada render
    const paginatedDateView = useMemo(() => {
        const startIndex = recordsPerView * currentPage - recordsPerView;
        const endIndex = recordsPerView * currentPage;
        return report.slice(startIndex, endIndex);
    }, [report, currentPage, recordsPerView]);

    const paginatedDisplayView = useMemo(() => {
        const startIndex = recordsPerView * currentPage - recordsPerView;
        const endIndex = recordsPerView * currentPage;
        return reportByDisplay.slice(startIndex, endIndex);
    }, [reportByDisplay, currentPage, recordsPerView]);

    const handleViewModeChange = useCallback((_: React.MouseEvent<HTMLElement>, newMode: 'date' | 'display' | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
            setCurrentPage(1);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        getReportData(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Efecto para actualizar la paginación automáticamente cuando cambia el reporte o modo de vista
    useEffect(() => {
        const itemCount = viewMode === 'date' ? report.length : reportByDisplay.length;
        setPaginationCount(Math.ceil(itemCount / recordsPerView));
    }, [report.length, reportByDisplay.length, viewMode, recordsPerView]);

    // Función para recuperar la información a presentar en el reporte
    const getReportData = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })
        setLoading(false);

        const report = response.data?.data?.inventory ?? []
        setReport(report);
        setBackupReports(report);

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
    }

    const changePaginationHandler = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    const changeSearchFilterHandler = (item: any, filterType: "sucursal" | "user") => {
        setSearchFilters((prevFilters) => {
            let updatedFilterItems: any[];

            if (item.key === 'all') {
                const newCheckedValue = !item.checked;
                updatedFilterItems = prevFilters[filterType].map((filterItem: any) => ({
                    ...filterItem,
                    checked: newCheckedValue,
                }));
            } else {
                updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                    if (filterItem.key === item.key) {
                        return { ...filterItem, checked: !filterItem.checked };
                    }
                    return { ...filterItem };
                });
                updatedFilterItems = normalizeAllCheckbox(updatedFilterItems);
            }

            const nextFilters = { ...prevFilters, [filterType]: updatedFilterItems };
            const filteredReport = filterInventoryReport(nextFilters);

            setReport(filteredReport);
            setCurrentPage(1);

            return nextFilters;
        })
    }

    const normalizeAllCheckbox = (items: any[]) => {
        const allIndex = items.findIndex((filterItem: any) => filterItem.key === 'all');
        if (allIndex === -1) return items;
        const allChecked = items
            .filter((filterItem: any) => filterItem.key !== 'all')
            .every((filterItem: any) => filterItem.checked);
        return items.map((filterItem: any, idx: number) =>
            idx === allIndex ? { ...filterItem, checked: allChecked } : filterItem
        );
    };

    const getDisplayIdentifier = (display: any) =>
        String(display?.displayKey ?? display?.userId ?? display?.id ?? display?.key ?? display?.name ?? '');

    const getSucursalIdentifier = (sucursal: any) =>
        String(sucursal?.sucursalId ?? sucursal?.id ?? sucursal?.key ?? sucursal?.name ?? '');

    type FilterSelection = { mode: 'all' | 'some' | 'none'; keys: string[] };

    const computeSelection = (items: any[]): FilterSelection => {
        if (!Array.isArray(items) || items.length === 0) return { mode: 'all', keys: [] };
        const allOption = items.find((item: any) => item.key === 'all');
        if (allOption?.checked) return { mode: 'all', keys: [] };
        const keys = items
            .filter((item: any) => item.key !== 'all' && item.checked)
            .map((item: any) => String(item.key));
        if (keys.length === 0) return { mode: 'none', keys: [] };
        return { mode: 'some', keys };
    };

    const filterInventoryReport = (filtersState: { user: any[]; sucursal: any[] }) => {
        const userSelection = computeSelection(filtersState.user);
        const sucursalSelection = computeSelection(filtersState.sucursal);

        if (userSelection.mode === 'none' || sucursalSelection.mode === 'none') return [];

        return backupReport.reduce((acc: any[], record: any) => {
            const filteredDisplays = (record?.displays ?? []).reduce((displayAcc: any[], display: any) => {
                if (userSelection.mode === 'some') {
                    const displayKey = getDisplayIdentifier(display);
                    if (!userSelection.keys.includes(displayKey)) return displayAcc;
                }

                const filteredSucursals = (display?.sucursals ?? []).filter((sucursal: any) => {
                    if (sucursalSelection.mode === 'all') return true;
                    const sucursalKey = getSucursalIdentifier(sucursal);
                    return sucursalSelection.keys.includes(sucursalKey);
                });

                if (sucursalSelection.mode !== 'all' && filteredSucursals.length === 0) return displayAcc;

                displayAcc.push({ ...display, sucursals: filteredSucursals });
                return displayAcc;
            }, []);

            if (filteredDisplays.length > 0) acc.push({ ...record, displays: filteredDisplays });
            return acc;
        }, []);
    };

    return (
        <Box>
            <Box ref={contentRef} sx={{ width: '100%', height: '100vh', overflow: 'auto' }}>
                <Box sx={{ width: '100%', height: '100vh', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                    {/* Menú lateral */}
                    <Box sx={{ gridColumn: 'span 2', borderRight: 2, borderColor: '#EBEBEB' }}>
                        <SidebarMenu pageName="inventory" />
                    </Box>

                    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', flexGrow: 1, gridColumn: 'span 10' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 2, borderColor: '#EBEBEB', px: 4, py: 3 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <InventoryIcon color="primary" sx={{ fontSize: 28 }} />
                                    <Typography variant="h5" color="primary" fontWeight="bold">Inventario</Typography>
                                </Stack>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={handleViewModeChange}
                                    size="medium"
                                    aria-label="modo de vista"
                                    sx={{
                                        bgcolor: 'background.paper',
                                        boxShadow: 2,
                                        borderRadius: 2,
                                        '& .MuiToggleButtonGroup-grouped': {
                                            border: 0,
                                            borderRadius: '8px !important',
                                            mx: 0.5,
                                            '&:not(:first-of-type)': {
                                                borderLeft: 0,
                                                ml: 0.5,
                                            },
                                            '&:first-of-type': {
                                                ml: 0.5,
                                            },
                                            '&:last-of-type': {
                                                mr: 0.5,
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        value="date"
                                        aria-label="agrupar por fecha"
                                        sx={{
                                            px: 2.5,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: '#EBEBEB',
                                                transform: 'translateY(-1px)',
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: '#183555',
                                                color: 'white',
                                                boxShadow: 3,
                                                '&:hover': {
                                                    bgcolor: '#122f47',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CalendarTodayIcon sx={{ fontSize: 18 }} />
                                            <Typography variant="body2" fontWeight={600}>Por Fecha</Typography>
                                        </Stack>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="display"
                                        aria-label="agrupar por display"
                                        sx={{
                                            px: 2.5,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: '#EBEBEB',
                                                transform: 'translateY(-1px)',
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: '#183555',
                                                color: 'white',
                                                boxShadow: 3,
                                                '&:hover': {
                                                    bgcolor: 'info.dark',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <GroupIcon sx={{ fontSize: 18 }} />
                                            <Typography variant="body2" fontWeight={600}>Por Display</Typography>
                                        </Stack>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CalendarTodayIcon color="action" />
                                <Typography variant="body1" fontWeight="600">Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</Typography>
                            </Stack>
                        </Box>

                        <Box
                            id="main-section"
                            sx={{ width: '100%', height: '100vh', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}
                        >
                            {loading ? (
                                <Box sx={{ gridColumn: 'span 12', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                    <CircularProgress color="primary" size={50} />
                                </Box>
                            ) : (
                                <>
                                    {report.length === 0 ?
                                        <Box sx={{ gridColumn: 'span 9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2, p: 4 }}>
                                            <img src={EmptyFolder} alt="empty" style={{ height: '200px', objectFit: 'contain' }} />
                                            <Typography variant="h6" textAlign="center" fontWeight="600" color="primary">
                                                No se encontraron resultados para tu búsqueda
                                            </Typography>
                                        </Box>
                                        :
                                        <Box sx={{ gridColumn: 'span 9', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                                            {viewMode === 'date' ? (
                                                // Vista agrupada por fechas
                                                <>
                                                    {paginatedDateView.map((repo: any, index: number) => (
                                                        <Card key={index} elevation={2}>
                                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Avatar sx={{ bgcolor: '#183555', width: 32, height: 32 }}>
                                                                            <CalendarTodayIcon sx={{ fontSize: 18, color: "#FFF" }} />
                                                                        </Avatar>
                                                                        <Typography variant="h6" fontWeight="600">{repo.date}</Typography>
                                                                    </Stack>
                                                                    <Chip
                                                                        icon={<PersonIcon />}
                                                                        label={`${repo.displays?.length} Display${repo.displays?.length !== 1 ? 's' : ''}`}
                                                                        color="primary"
                                                                        size="small"
                                                                    />
                                                                </Box>

                                                                {repo.displays.map((display: any, indx: number) => {
                                                                    const displayKey = `${index}_${indx}`;
                                                                    const isDisplayExpanded = expandedDisplays[displayKey] || false;

                                                                    return (
                                                                        <DisplayCard
                                                                            key={displayKey}
                                                                            display={display}
                                                                            displayKey={displayKey}
                                                                            isDisplayExpanded={isDisplayExpanded}
                                                                            expandedSucursals={expandedSucursals}
                                                                            onToggleDisplay={() => handleToggleDisplay(displayKey)}
                                                                            onToggleSucursal={handleToggleSucursal}
                                                                        />
                                                                    );
                                                                })}
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </>
                                            ) : (
                                                // Vista agrupada por displays
                                                <>
                                                    {paginatedDisplayView.map((displayGroup: any, index: number) => (
                                                        <Card key={index} elevation={2}>
                                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Avatar sx={{ bgcolor: '#183555', width: 32, height: 32 }}>
                                                                            <GroupIcon sx={{ fontSize: 18 }} />
                                                                        </Avatar>
                                                                        <Typography variant="h6" fontWeight="600">{displayGroup.displayName}</Typography>
                                                                    </Stack>
                                                                    <Chip
                                                                        icon={<CalendarTodayIcon />}
                                                                        label={`${displayGroup.dates?.length} Fecha${displayGroup.dates?.length !== 1 ? 's' : ''}`}
                                                                        color="info"
                                                                        size="small"
                                                                    />
                                                                </Box>

                                                                {displayGroup.dates.map((dateInfo: any, indx: number) => {
                                                                    const dateKey = `${index}_${indx}`;
                                                                    const isDateExpanded = expandedDisplays[dateKey] || false;

                                                                    return (
                                                                        <DateCard
                                                                            key={dateKey}
                                                                            dateData={dateInfo}
                                                                            dateKey={dateKey}
                                                                            isDateExpanded={isDateExpanded}
                                                                            expandedSucursals={expandedSucursals}
                                                                            onToggleDate={() => handleToggleDisplay(dateKey)}
                                                                            onToggleSucursal={handleToggleSucursal}
                                                                        />
                                                                    );
                                                                })}
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </>
                                            )}

                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto', py: 3 }}>
                                                <Pagination
                                                    size="large"
                                                    color="primary"
                                                    count={paginationCount}
                                                    defaultPage={1}
                                                    page={currentPage}
                                                    siblingCount={1}
                                                    boundaryCount={2}
                                                    onChange={changePaginationHandler}
                                                />
                                            </Box>
                                        </Box>
                                    }

                                    <Box sx={{ gridColumn: 'span 3', overflowY: 'auto', display: 'flex', p: 2, flexDirection: 'column', gap: 1.5, borderLeft: 2, borderColor: '#EBEBEB', bgcolor: '#fafbfc' }}>
                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-fecha-content"
                                                id="panel-fecha-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CalendarTodayIcon fontSize="small" sx={{ color: '#1565c0' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Fecha</Typography>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={3} alignItems="center">
                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de inicio</Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            value={formatDateToISODateString(startDate)}
                                                            inputProps={{
                                                                min: formatDateToISODateString(systemMin),
                                                                max: formatDateToISODateString(finishDate)
                                                            }}
                                                            onChange={(e: any) => {
                                                                const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                                if (!selectedDate) return;
                                                                const [s, f] = normalizeWithAnchor(selectedDate, finishDate, 'start');
                                                                setStartDate(s ?? new Date());
                                                                setFinishDate(f ?? new Date());
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>Fecha de fin</Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            value={formatDateToISODateString(finishDate)}
                                                            inputProps={{
                                                                min: formatDateToISODateString(systemMin),
                                                                max: formatDateToISODateString(addMonths(startDate, MAX_RANGE_MONTHS) > today ? today : addMonths(startDate, MAX_RANGE_MONTHS))
                                                            }}
                                                            onChange={(e: any) => {
                                                                const selectedDate = e.target.value ? formatISODateStringToDate(e.target.value, false) : new Date();
                                                                if (!selectedDate) return;
                                                                const [s, f] = normalizeWithAnchor(startDate, selectedDate, 'end');
                                                                setStartDate(s ?? new Date());
                                                                setFinishDate(f ?? new Date());
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ width: '100%' }}>
                                                        <SolidButton text="Aplicar" backgroundColor="primary" disabled={false} onClickHandler={() => {
                                                            getReportData(new AbortController().signal);
                                                        }} />
                                                    </Box>
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-displays-content"
                                                id="panel-displays-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Displays</Typography>
                                                    <Chip
                                                        label={searchFilters.user.length}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#e8f5e9',
                                                            color: '#2e7d32',
                                                            fontWeight: 600,
                                                            border: '1px solid #a5d6a7'
                                                        }}
                                                    />
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {searchFilters.user.length === 0 ? (
                                                    <Typography variant="body2" fontWeight="600" color="primary">No hay datos disponibles</Typography>
                                                ) : (
                                                    <Box>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Buscar display..."
                                                            value={displaySearch}
                                                            onChange={(e) => {
                                                                setDisplaySearch(e.target.value);
                                                                setDisplayPage(1);
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                            }}
                                                            sx={{ mb: 2 }}
                                                        />
                                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                            {searchFilters.user
                                                                .filter((item: any) =>
                                                                    (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                                )
                                                                .slice((displayPage - 1) * filtersPerPage, displayPage * filtersPerPage)
                                                                .map((item: any) => (
                                                                    <ListItem key={item.key} dense disablePadding>
                                                                        <FormControlLabel
                                                                            sx={{ width: '100%' }}
                                                                            label={<Typography variant="body2">{item.name + " " + item.lastName}</Typography>}
                                                                            control={
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    id={item.key}
                                                                                    name={item.name + " " + item.lastName}
                                                                                    value={item.key}
                                                                                    checked={item.checked}
                                                                                    onChange={(event) => changeSearchFilterHandler(item, "user")}
                                                                                />
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            }
                                                        </List>
                                                        {Math.ceil(searchFilters.user.filter((item: any) =>
                                                            (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                        ).length / filtersPerPage) > 1 && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                                    <Pagination
                                                                        size="small"
                                                                        count={Math.ceil(searchFilters.user.filter((item: any) =>
                                                                            (item.name + " " + item.lastName).toLowerCase().includes(displaySearch.toLowerCase())
                                                                        ).length / filtersPerPage)}
                                                                        page={displayPage}
                                                                        onChange={(e, page) => setDisplayPage(page)}
                                                                    />
                                                                </Box>
                                                            )}
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion sx={{
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:before': { display: 'none' },
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#183555' }} />}
                                                aria-controls="panel-sucursales-content"
                                                id="panel-sucursales-header"
                                                sx={{ bgcolor: '#f5f7fa' }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <StoreIcon fontSize="small" sx={{ color: '#f57c00' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#183555">Sucursales</Typography>
                                                    <Chip
                                                        label={searchFilters.sucursal.length}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#fff3e0',
                                                            color: '#f57c00',
                                                            fontWeight: 600,
                                                            border: '1px solid #ffcc80'
                                                        }}
                                                    />
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {searchFilters.sucursal.length === 0 ? (
                                                    <Typography variant="body2" fontWeight="600" color="primary">No hay datos disponibles</Typography>
                                                ) : (
                                                    <Box>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Buscar sucursal..."
                                                            value={sucursalSearch}
                                                            onChange={(e) => {
                                                                setSucursalSearch(e.target.value);
                                                                setSucursalPage(1);
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                            }}
                                                            sx={{ mb: 2 }}
                                                        />
                                                        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                            {searchFilters.sucursal
                                                                .filter((item: any) =>
                                                                    item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                                )
                                                                .slice((sucursalPage - 1) * filtersPerPage, sucursalPage * filtersPerPage)
                                                                .map((item: any) => (
                                                                    <ListItem key={item.key} dense disablePadding>
                                                                        <FormControlLabel
                                                                            sx={{ width: '100%' }}
                                                                            label={<Typography variant="body2">{item.name}</Typography>}
                                                                            control={
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    id={item.key}
                                                                                    name={item.name}
                                                                                    value={item.key}
                                                                                    checked={item.checked}
                                                                                    onChange={(event) => changeSearchFilterHandler(item, "sucursal")}
                                                                                />
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            }
                                                        </List>
                                                        {Math.ceil(searchFilters.sucursal.filter((item: any) =>
                                                            item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                        ).length / filtersPerPage) > 1 && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                                    <Pagination
                                                                        size="small"
                                                                        count={Math.ceil(searchFilters.sucursal.filter((item: any) =>
                                                                            item.name.toLowerCase().includes(sucursalSearch.toLowerCase())
                                                                        ).length / filtersPerPage)}
                                                                        page={sucursalPage}
                                                                        onChange={(e, page) => setSucursalPage(page)}
                                                                    />
                                                                </Box>
                                                            )}
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
